import React, { createContext, useContext, useState, useEffect } from 'react';
import { mockResources } from '../data/mockResources';
import { mockCourses } from '../data/mockCourses';
import { mockPathways } from '../data/mockPathways';
import { mockPosts } from '../data/mockPosts';
import { mockExperts } from '../data/mockExperts';
import { mockShowcases } from '../data/mockShowcases';
import { mockEvents } from '../data/mockEvents';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  // User progress and state
  const [courseProgress, setCourseProgress] = useState(() => {
    const stored = localStorage.getItem('gc_course_progress');
    return stored ? JSON.parse(stored) : {};
  });

  const [pathwayProgress, setPathwayProgress] = useState(() => {
    const stored = localStorage.getItem('gc_pathway_progress');
    return stored ? JSON.parse(stored) : {};
  });

  const [savedPacks, setSavedPacks] = useState(() => {
    const stored = localStorage.getItem('gc_saved_packs');
    return stored ? JSON.parse(stored) : [];
  });

  const [chatSessions, setChatSessions] = useState(() => {
    const stored = localStorage.getItem('gc_chat_sessions');
    return stored ? JSON.parse(stored) : [];
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem('gc_course_progress', JSON.stringify(courseProgress));
  }, [courseProgress]);

  useEffect(() => {
    localStorage.setItem('gc_pathway_progress', JSON.stringify(pathwayProgress));
  }, [pathwayProgress]);

  useEffect(() => {
    localStorage.setItem('gc_saved_packs', JSON.stringify(savedPacks));
  }, [savedPacks]);

  useEffect(() => {
    localStorage.setItem('gc_chat_sessions', JSON.stringify(chatSessions));
  }, [chatSessions]);

  // Methods
  const updateCourseProgress = (courseId, lessonId) => {
    setCourseProgress(prev => {
      const course = prev[courseId] || { completedLessons: [] };
      if (!course.completedLessons.includes(lessonId)) {
        return {
          ...prev,
          [courseId]: {
            ...course,
            completedLessons: [...course.completedLessons, lessonId]
          }
        };
      }
      return prev;
    });
  };

  const updatePathwayProgress = (pathwayId, stepId) => {
    setPathwayProgress(prev => {
      const pathway = prev[pathwayId] || { completedSteps: [] };
      if (!pathway.completedSteps.includes(stepId)) {
        return {
          ...prev,
          [pathwayId]: {
            ...pathway,
            completedSteps: [...pathway.completedSteps, stepId]
          }
        };
      }
      return prev;
    });
  };

  const savePack = (pack) => {
    setSavedPacks(prev => [pack, ...prev]);
  };

  const removePack = (packId) => {
    setSavedPacks(prev => prev.filter(p => p.id !== packId));
  };

  const addChatSession = (session) => {
    setChatSessions(prev => [session, ...prev]);
  };

  const value = {
    // Data
    resources: mockResources,
    courses: mockCourses,
    pathways: mockPathways,
    posts: mockPosts,
    experts: mockExperts,
    showcases: mockShowcases,
    events: mockEvents,
    
    // User state
    courseProgress,
    pathwayProgress,
    savedPacks,
    chatSessions,
    
    // Methods
    updateCourseProgress,
    updatePathwayProgress,
    savePack,
    removePack,
    addChatSession
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
